/**
 * AI Status Panel
 * Displays comprehensive system status and diagnostic information
 */

import React from 'react'
import { CheckCircle, AlertCircle, Server, Database, Zap, Info } from 'lucide-react'

export default function AIStatusPanel({ systemStatus, config }) {
  if (!systemStatus || !config) return null

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div className="space-y-6">
      {/* System Overview */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Server className="w-5 h-5 text-blue-400" />
          System Overview
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Groq Service Details */}
          <div className="bg-slate-700/30 rounded-lg border border-slate-600 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-300">Groq (Cloud)</h3>
              {systemStatus.groq.available && systemStatus.groq.enabled ? (
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              ) : systemStatus.groq.enabled ? (
                <AlertCircle className="w-5 h-5 text-orange-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-400" />
              )}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Enabled:</span>
                <span className={systemStatus.groq.enabled ? 'text-emerald-400' : 'text-red-400'}>
                  {systemStatus.groq.enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Available:</span>
                <span className={systemStatus.groq.available ? 'text-emerald-400' : 'text-red-400'}>
                  {systemStatus.groq.available ? 'Connected' : 'Offline'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Model:</span>
                <span className="text-blue-300 font-mono text-xs">mixtral-8x7b-32768</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Purpose:</span>
                <span className="text-slate-300">Analysis & Insights</span>
              </div>
            </div>
          </div>

          {/* Ollama Service Details */}
          <div className="bg-slate-700/30 rounded-lg border border-slate-600 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-purple-300">Ollama (Local)</h3>
              {systemStatus.ollama.available && systemStatus.ollama.enabled ? (
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              ) : systemStatus.ollama.enabled ? (
                <AlertCircle className="w-5 h-5 text-orange-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-400" />
              )}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Enabled:</span>
                <span className={systemStatus.ollama.enabled ? 'text-emerald-400' : 'text-red-400'}>
                  {systemStatus.ollama.enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Available:</span>
                <span className={systemStatus.ollama.available ? 'text-emerald-400' : 'text-red-400'}>
                  {systemStatus.ollama.available ? 'Running' : 'Not running'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Model:</span>
                <span className="text-purple-300 font-mono text-xs">neural-chat</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Purpose:</span>
                <span className="text-slate-300">Voice & Learning</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Learning & Context Stats */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Database className="w-5 h-5 text-cyan-400" />
          Learning System Status
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Context Size */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Context Size</span>
              <Zap className="w-4 h-4 text-yellow-400" />
            </div>
            <p className="text-2xl font-bold text-yellow-400">{formatBytes(systemStatus.contextSize)}</p>
            <p className="text-xs text-slate-500 mt-1">of ~500 KB available</p>
          </div>

          {/* Feedback Entries */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Feedback Entries</span>
              <Info className="w-4 h-4 text-blue-400" />
            </div>
            <p className="text-2xl font-bold text-blue-400">{systemStatus.feedbackCount}</p>
            <p className="text-xs text-slate-500 mt-1">for model fine-tuning</p>
          </div>

          {/* User Profiles */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">User Profiles</span>
              <CheckCircle className="w-4 h-4 text-green-400" />
            </div>
            <p className="text-2xl font-bold text-green-400">{systemStatus.userProfiles}</p>
            <p className="text-xs text-slate-500 mt-1">learning patterns</p>
          </div>
        </div>
      </div>

      {/* Connection Guide */}
      <div className="bg-amber-900/20 rounded-xl border border-amber-500/30 p-6">
        <h3 className="text-lg font-semibold text-amber-300 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Setup Requirements
        </h3>

        <div className="space-y-3 text-sm">
          <div>
            <p className="text-amber-200 font-medium mb-1">✓ Groq Setup:</p>
            <ol className="list-decimal list-inside text-amber-100/80 space-y-1 ml-2">
              <li>Get free API key from console.groq.com</li>
              <li>Set REACT_APP_GROQ_API_KEY in .env.local</li>
              <li>Implement backend proxy (see AI_SETUP_GUIDE.md)</li>
            </ol>
          </div>

          <div>
            <p className="text-amber-200 font-medium mb-1">✓ Ollama Setup:</p>
            <ol className="list-decimal list-inside text-amber-100/80 space-y-1 ml-2">
              <li>Download from ollama.ai</li>
              <li>Run: ollama serve</li>
              <li>Pull model: ollama pull neural-chat</li>
            </ol>
          </div>

          <div className="mt-4 p-3 bg-slate-800 rounded border border-slate-600 font-mono text-xs text-slate-300">
            Last updated: {new Date(systemStatus.timestamp).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  )
}
